import React, { useEffect, useState } from "react";
import { Buffer } from "buffer";
import { Container } from "reactstrap";
import { ToastContainer, toast } from "react-toastify";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { logo } from "../../Data/Images/Home";
import CustomLink from "../CustomLink";
import { useGetContentsQuery } from "../../features/contentsApiSlice";
import { useResendCreatePasswordEmailMutation } from "../../features/auth/authApiSlice";

const RESEND_COOLDOWN_SECONDS = 180;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function formatCooldown(seconds) {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
}

const RequestCreatePasswordLink = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const idImage = Buffer.from("58").toString("base64");
  const initialEmail = searchParams.get("email") || "";

  const [email, setEmail] = useState(initialEmail);
  const [backgroundImage, setBackgroundImage] = useState("");
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [resendCreatePasswordEmail, { isLoading }] = useResendCreatePasswordEmailMutation();

  const { data: fetchHeaderImage, isSuccess: isSuccessImage } =
    useGetContentsQuery({ id: idImage, lang: "en", skip: false });

  useEffect(() => {
    if (!isSuccessImage || !fetchHeaderImage?.data?.picture) return;

    setBackgroundImage(
      `${process.env.REACT_APP_PUBLIC_URL}${fetchHeaderImage.data.picture}`
    );
  }, [fetchHeaderImage, isSuccessImage]);

  useEffect(() => {
    if (cooldownSeconds <= 0) return undefined;

    const timerId = setInterval(() => {
      setCooldownSeconds((currentSeconds) => Math.max(currentSeconds - 1, 0));
    }, 1000);

    return () => clearInterval(timerId);
  }, [cooldownSeconds]);

  const handleRequestNewLink = async (e) => {
    e.preventDefault();

    if (!email) {
      toast.error(t("Auth_EmailRequired"));
      return;
    }

    if (!EMAIL_PATTERN.test(email)) {
      toast.error(t("Auth_ValidEmail"));
      return;
    }

    if (cooldownSeconds > 0) {
      return;
    }

    try {
      const response = await resendCreatePasswordEmail({ email }).unwrap();
      setCooldownSeconds(RESEND_COOLDOWN_SECONDS);
      toast.success(response?.message || t("Auth_RequestCreatePasswordLink_Sent"));
    } catch (err) {
      toast.error(err?.data?.message || t("Auth_GenericError"));
    }
  };

  return (
    <>
      <Container
        fluid={true}
        className="login-section section-padding t-pt-4 md:t-pt-8"
        style={{
          backgroundImage: `linear-gradient(var(--secondary-80),var(--secondary-80)) , url(${backgroundImage})`,
        }}
      >
        <div className="container d-flex t-flex-col align-items-center justify-content-center vh-100 t-gap-20 md:-t-mt-10">
          <div className="mlogo t-mb-2">
            <CustomLink to="/">
              <img src={logo} alt="" width={120} />
            </CustomLink>
          </div>
          <div className="t-w-full t-max-w-[400px] t-mx-auto t-bg-gray-50 t-flex t-flex-col t-gap-16 t-shadow-lg t-rounded-md !t-py-12 t-px-4">
            <div className="t-text-2xl t-font-bold t-text-center t-text-tertiary">
              {t("Auth_RequestCreatePasswordLink_Title")}
            </div>
            <form className="t-flex t-flex-col t-gap-8" onSubmit={handleRequestNewLink}>
              <p className="!t-m-0 !t-text-base t-text-center t-text-gray-600">
                {t("Auth_RequestCreatePasswordLink_Help")}
              </p>
              <div className="row textboxes">
                <div className="col-12 position-relative">
                  <input
                    type="email"
                    className="form-control !t-text-base"
                    placeholder={t("Email")}
                    aria-describedby="helpId"
                    onChange={(e) => setEmail(e.target.value)}
                    value={email}
                  />
                  <i className="fa fas fa-envelope !t-text-lg !t-mt-3" />
                </div>
              </div>
              <div className="row">
                <div className="col-12 text-center">
                  <button
                    type="submit"
                    className="col-12 btn btn-custom-secondary fw-bold lts1 px-5 !t-text-base"
                    disabled={isLoading || cooldownSeconds > 0}
                  >
                    {isLoading
                      ? t("Auth_RequestCreatePasswordLink_Sending")
                      : t("Auth_RequestCreatePasswordLink_Button")}
                  </button>
                  {cooldownSeconds > 0 && (
                    <p className="!t-mt-3 !t-mb-0 !t-text-base t-text-gray-600">
                      {t("Auth_RequestCreatePasswordLink_Timer", {
                        time: formatCooldown(cooldownSeconds),
                      })}
                    </p>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </Container>
      <ToastContainer />
    </>
  );
};

export default RequestCreatePasswordLink;
